import React, { useEffect, useState } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import { useFormik } from "formik";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import { TempleServiceAdd } from "../../services/temple-service.services";
import { TempleList } from "../../services/temple.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Select from "react-select";

const ServiceAddTemple = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [temples, setTemples] = useState([]);

  const breadcrumbs = [
    { title: t("Dashboard"), url: "/admin/dashboard" },
    { title: t("Temple"), url: "/admin/temples/list/1" },
    { title: t("Service"), url: "/admin/temple-service/list/1" },
    { title: t("Add"), url: "" },
  ];

  useEffect(() => {
    TempleList({}).then((res) => {
      setTemples(res?.data?.list || []);
    });
  }, []);

  const validationSchema = Yup.object({
    templeId: Yup.string()
      .required("Temple is required"),
    name: Yup.string()
      .required("Service name is required")
      .trim("Service name cannot be empty"),
    serviceType: Yup.string()
      .required("Service type is required")
      .trim("Service type cannot be empty"),
    duration: Yup.string()
      .required("Duration is required")
      .trim("Duration cannot be empty"),
    price: Yup.number()
      .typeError("Price must be a number")
      .required("Price is required")
      .min(10, "Price must be at least 10")
      .test(
        "no-space",
        "Price cannot contain spaces",
        (value) => value !== undefined && !/\s/.test(String(value))
      ),
    description: Yup.string()
      .required("Description is required")
      .trim("Description cannot be empty"),
  });


  const formik = useFormik({
    initialValues: {
      templeId: "",
      name: "",
      serviceType: "",
      duration: "",
      description: "",
      price: "",
    },
    validationSchema,
    onSubmit: (values) => {
      globalLoader(true);
      setSubmitted(true);

      TempleServiceAdd(values)
        .then((res) => {
          globalLoader(false);

          if (res?.success) {
            Swal.fire({
              icon: "success",
              text: res.message || "Service added successfully",
              ...SWAL_SETTINGS,
            });
            navigate("/admin/temple-service/list/1");
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(res) || "Something went wrong",
              ...SWAL_SETTINGS,
            });
            setSubmitted(false);
          }
        })
        .catch((error) => {
          globalLoader(false);
          setSubmitted(false);

          Swal.fire({
            icon: "error",
            text:
              error?.response?.data?.message ||
              error?.message ||
              "Server error, please try again",
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  const templeOptions = temples.map((t) => ({
    value: t._id,
    label: `${t.templeName} (${t.templeLocation})`,
  }));

  const durationOptions = [10, 15, 20, 30, 45, 60, 90, 120, 150, 180];

  const formatDurationLabel = (minutes) => {
    if (minutes < 60) return `${minutes} Minute`;

    const hours = minutes / 60;
    return `${minutes} Minute - ${hours} Hour`;
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="card custom-card">
          <div className="card-body">
            <h6 className="main-content-label mb-3">Add Temple Service</h6>

            <div className="form-group">
              <label>
                {t("Temple")} <span className="requirestar">*</span>
              </label>

              <Select
                options={templeOptions}
                placeholder="Search & select temple..."
                isSearchable
                onChange={(selected) =>
                  formik.setFieldValue("templeId", selected?.value || "")
                }
              />

              <span className="text-danger d-flex text-left">
                <CustomError name="templeId" form={formik} />
              </span>
            </div>

            <div className="form-group">
              <label>
                Service Name <span className="requirestar">*</span>
              </label>
              <input
                className="form-control"
                name="name"
                onChange={formik.handleChange}
              />
              <span className="text-danger d-flex text-left">
                <CustomError name="name" form={formik} />
              </span>
            </div>

            <div className="form-group">
              <label>
                Service Type <span className="requirestar">*</span>
              </label>
              <input
                className="form-control"
                name="serviceType"
                onChange={formik.handleChange}
              />
              <span className="text-danger d-flex text-left">
                <CustomError name="serviceType" form={formik} />
              </span>
            </div>

            <div className="form-group">
              <label>
                Duration <span className="requirestar">*</span>
              </label>
              <select
                name="duration"
                className="form-control"
                value={formik.values.duration}
                onChange={(e) =>
                  formik.setFieldValue("duration", Number(e.target.value))
                }
              >
                <option value="">Select Duration</option>

                {durationOptions.map((min) => (
                  <option key={min} value={min}>
                    {formatDurationLabel(min)}
                  </option>
                ))}
              </select>

              <span className="text-danger d-flex text-left">
                <CustomError name="duration" form={formik} />
              </span>
            </div>

            <div className="form-group">
              <label>
                Price <span className="requirestar">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                name="price"
                onChange={formik.handleChange}
              />

              <span className="text-danger d-flex text-left">
                <CustomError name="price" form={formik} />
              </span>
            </div>

            <div className="form-group">
              <label>
                Description <span className="requirestar">*</span>
              </label>
              <textarea
                className="form-control"
                name="description"
                onChange={formik.handleChange}
              />

              <span className="text-danger d-flex text-left">
                <CustomError name="description" form={formik} />
              </span>
            </div>

            <button className="btn btn-info me-2" type="submit">
              Submit
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ServiceAddTemple;
